import type {
	HourBucket,
	PlausibilityPrior,
	Season,
	TaxonomyClass,
} from "@sitescene/types";

import { type PriorWriteInput } from "@/features/plausibility/use-priors";

export const SEASONS: Season[] = ["winter", "spring", "summer", "autumn"];
export const HOUR_BUCKETS: HourBucket[] = [
	"night",
	"morning",
	"afternoon",
	"evening",
];

/** A whole new prior: its identity plus its value. */
export interface PriorDraft extends PriorValueDraft {
	classId: string;
	hourBucket: "" | HourBucket;
	season: "" | Season;
}

/** What a person types for a prior's value: probability as a percent, and notes. */
export interface PriorValueDraft {
	notes: string;
	percent: string;
}

export const logOddsToPercentInput = (logOdds: number): string => {
	const p = 1 / (1 + Math.exp(-logOdds));
	return (p * 100).toFixed(2).replace(/\.?0+$/, "");
};

export const valueDraftFromPrior = (
	prior: PlausibilityPrior,
): PriorValueDraft => ({
	notes: prior.notes ?? "",
	percent: logOddsToPercentInput(prior.logPriorOdds),
});

export const EMPTY_DRAFT: PriorDraft = {
	classId: "",
	hourBucket: "",
	notes: "",
	percent: "1",
	season: "",
};

/** Log odds of the drafted percent, or NaN when it is not a probability strictly between 0 and 1. */
export const draftLogOdds = (draft: PriorValueDraft): number => {
	const p = Number(draft.percent) / 100;
	return p > 0 && p < 1 ? Math.log(p / (1 - p)) : NaN;
};

export const isValueDraftValid = (draft: PriorValueDraft): boolean =>
	!Number.isNaN(draftLogOdds(draft));

export const isDraftValid = (
	draft: PriorDraft,
	classes: TaxonomyClass[],
): boolean =>
	classes.some((cls) => cls.classId === draft.classId) &&
	isValueDraftValid(draft);

export const draftToWriteInput = (draft: PriorDraft): PriorWriteInput => ({
	classId: draft.classId,
	hourBucket: draft.hourBucket || undefined,
	logPriorOdds: Number(draftLogOdds(draft).toFixed(3)),
	notes: draft.notes.trim() || undefined,
	season: draft.season || undefined,
});

/**
 * The write that keeps a row's identity and replaces its value. Rows come
 * back with null for an unset season or hours; the API accepts only the
 * value or nothing, so nulls are dropped.
 */
export const valueDraftToWriteInput = (
	prior: PlausibilityPrior,
	draft: PriorValueDraft,
): PriorWriteInput => ({
	classId: prior.classId,
	hourBucket: prior.hourBucket ?? undefined,
	logPriorOdds: Number(draftLogOdds(draft).toFixed(3)),
	notes: draft.notes.trim() || undefined,
	season: prior.season ?? undefined,
});
